import {
  AirplaneLandingIcon,
  AirplaneTakeoffIcon,
  ArrowsLeftRightIcon,
  CalendarBlankIcon,
  CircleDashedIcon,
  ClockCounterClockwiseIcon,
  CoinsIcon,
  EyeIcon,
  GiftIcon,
  GlobeHemisphereWestIcon,
  HandbagIcon,
  LightningIcon,
  MoneyIcon,
  NotepadIcon,
  PackageIcon,
  ReceiptIcon,
  ScalesIcon,
  StorefrontIcon,
  TagIcon,
  ThumbsUpIcon,
  TimerIcon,
  TruckIcon,
  TShirtIcon,
  WarehouseIcon,
  WarningCircleIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react'

import { ChatAlertIcon, PackageCheckIcon, PackageShippedIcon } from '@reveni-io/reveni-ui'

import { filterNames as billingFilterNames } from '@/billing/pages/BillingPage/constants'
import { filterNames as configFilterNames } from '@/configuration/components/Filters/constants'
import { filterNames as inventoryFilterNames } from '@/inventory/pages/InventoryPage/constants'
import { filterNames as parcelFilterNames } from '@/parcels/pages/ParcelsPage/pages/ParcelsListPage/constants'
import { filterNames as returnsFilterNames } from '@/returns/pages/TransactionsPage/constants'
import { getAmountFilterLabel, getAmountMatchType } from '@/returns/pages/TransactionsPage/Filters/AmountFilter/helpers'
import { amountFilterConfig } from '@/returns/pages/TransactionsPage/Filters/constants'
import { orderTypeMenuItems } from '@/returns/pages/TransactionsPage/Filters/OrderTypeSelector/constants'
import { filterLevels } from '@/returns/pages/TransactionsPage/Filters/ReturnReasonFilter/constants'

import {
  getAlertTypeLabels,
  getBillingStatusName,
  getBillingTypeName,
  getCarrierServiceLabel,
  getCarriersLabel,
  getCategoryLabel,
  getClaimStatusLabels,
  getClaimTypeLabels,
  getCountryName,
  getCountryNames,
  getDatesLabel,
  getDeliveryMethodName,
  getDeliveryMethodTypeNames,
  getExchangeForOrderStatusName,
  getExchangeTypeOptions,
  getExportPriceStatusLabels,
  getForReviewReasonNames,
  getIsGiftLabel,
  getLocationLabel,
  getMissingInformationLabel,
  getMissingInformationValue,
  getNamesByIds,
  getNormalizedTrackingStatusLabels,
  getParcelTypeLabels,
  getPaymentStatusName,
  getPolicyRulesNames,
  getProductStatusLabel,
  getRefundMethodNames,
  getRefundStatusName,
  getRegionsNames,
  getRejectReasonNames,
  getReturnReasonNames,
  getReturnReasonTypeNames,
  getShipmentExceptionsOptions,
  getShipmentStatusLabels,
  getShippingLabelStatusName,
  getStatusesName,
  getTransactionTypeName,
  getUnitCostLabel,
  getWeightLabel,
  truncateList,
} from './helpers'

export const chips = [
  {
    show: filters => filters.transactionType && filters.transactionType.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipo de transacción')}: </strong>
        {getTransactionTypeName(filters.transactionType, t)}
      </>
    ),
    Icon: ArrowsLeftRightIcon,
    name: returnsFilterNames.transactionType,
  },
  {
    show: filters => filters.instant,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Modo de transacción')}: </strong>
        {filters.instant === 'true' ? t('Instantáneo') : t('No instantáneo')}
      </>
    ),
    Icon: LightningIcon,
    name: returnsFilterNames.instant,
  },
  {
    show: filters => filters.status && filters.status.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado')}: </strong>
        {getStatusesName(filters.status, t)}
      </>
    ),
    Icon: CircleDashedIcon,
    name: returnsFilterNames.status,
  },
  {
    show: filters => filters.deliveryMethodType && filters.deliveryMethodType.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipo de método de devolución')}: </strong>
        {getDeliveryMethodTypeNames(filters.deliveryMethodType, t)}
      </>
    ),
    Icon: TruckIcon,
    name: returnsFilterNames.deliveryMethodType,
  },
  {
    show: filters => filters.deliveryMethod,
    label: ({ filters, t, deliveryMethods }) => (
      <>
        <strong>{t('Método de devolución')}: </strong>
        {getDeliveryMethodName(filters.deliveryMethod, deliveryMethods)}
      </>
    ),
    Icon: TruckIcon,
    name: returnsFilterNames.deliveryMethod,
  },
  {
    show: filters => filters.refundDates && (filters.refundDates.startDate || filters.refundDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de solicitud')}: </strong>
        {getDatesLabel(filters.refundDates, t, language)}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.refundDate,
  },
  {
    show: filters => filters.refundDatesDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de solicitud')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.refundDatesDaysGte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.refundDatesDaysGte,
  },
  {
    show: filters => filters.refundDatesDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de solicitud')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.refundDatesDaysLte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.refundDatesDaysLte,
  },
  {
    show: filters => filters.lastUpdatedGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Última actualización')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.lastUpdatedGte })}
      </>
    ),
    Icon: ClockCounterClockwiseIcon,
    name: returnsFilterNames.lastUpdatedGte,
  },
  {
    show: filters => filters.lastUpdatedLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Última actualización')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.lastUpdatedLte })}
      </>
    ),
    Icon: ClockCounterClockwiseIcon,
    name: returnsFilterNames.lastUpdatedLte,
  },
  {
    show: filters => filters.expiresIn,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Días para validar')}: </strong>
        {t('{{expiresIn}} días o menos', { expiresIn: filters.expiresIn })}
      </>
    ),
    Icon: TimerIcon,
    name: returnsFilterNames.expiresIn,
  },
  {
    show: filters => filters.refundMethod && filters.refundMethod.length,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Método de reembolso')}: </strong>
        {getRefundMethodNames(filters.refundMethod, t)}
      </>
    ),
    Icon: MoneyIcon,
    name: returnsFilterNames.refundMethod,
  },
  {
    show: filters => filters.returnReason?.length,
    label: ({ filters, t, language, returnReasons }) => {
      const isReasonType = filters.returnReasonFilterLevel === filterLevels.reasonType
      const label = isReasonType ? t('Tipo de motivo de devolución') : t('Motivo de devolución')
      const value = isReasonType
        ? getReturnReasonTypeNames(filters.returnReason, language, t)
        : getReturnReasonNames(filters.returnReason, language, returnReasons, t)

      return (
        <>
          <strong>{label}: </strong>
          {value}
        </>
      )
    },
    Icon: NotepadIcon,
    name: returnsFilterNames.returnReason,
  },
  {
    show: filters => filters.returnReasonType && filters.returnReasonType.length,
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Tipo de motivo de devolución')}: </strong>
        {getReturnReasonTypeNames(filters.returnReasonType, language, t)}
      </>
    ),
    Icon: NotepadIcon,
    name: returnsFilterNames.returnReasonType,
  },
  {
    show: filters => filters.rejectReason && filters.rejectReason.length,
    label: ({ filters, t, rejectReasons }) => (
      <>
        <strong>{t('Motivo de rechazo')}: </strong>
        {getRejectReasonNames(filters.rejectReason, rejectReasons, t)}
      </>
    ),
    Icon: XCircleIcon,
    name: returnsFilterNames.rejectReason,
  },
  {
    show: filters => filters.forReviewReason && filters.forReviewReason.length,
    label: ({ filters, t, forReviewReasons }) => (
      <>
        <strong>{t('Motivo de revisión')}: </strong>
        {getForReviewReasonNames(filters.forReviewReason, forReviewReasons, t)}
      </>
    ),
    Icon: EyeIcon,
    name: returnsFilterNames.forReviewReason,
  },
  {
    show: filters => filters.region && filters.region.length,
    label: ({ filters, t, regions }) => (
      <>
        <strong>{t('Región')}: </strong>
        {getRegionsNames(filters.region, regions, t)}
      </>
    ),
    Icon: GlobeHemisphereWestIcon,
    name: returnsFilterNames.region || configFilterNames.region,
  },
  {
    show: filters => filters.billingType,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipo factura')}: </strong>
        {t(getBillingTypeName(filters.billingType))}
      </>
    ),
    Icon: ReceiptIcon,
    name: billingFilterNames.type,
  },
  {
    show: filters => filters.billingStatus && filters.billingStatus.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado')}: </strong>
        {getBillingStatusName(filters.billingStatus, t)}
      </>
    ),
    Icon: CircleDashedIcon,
    name: billingFilterNames.status,
  },
  {
    show: filters => filters.invoiceDates && (filters.invoiceDates.startDate || filters.invoiceDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha factura')}: </strong>
        {getDatesLabel(filters.invoiceDates, t, language)}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: billingFilterNames.invoiceDates,
  },
  {
    show: filters => filters.collectedDates && (filters.collectedDates.startDate || filters.collectedDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha pago')}: </strong>
        {getDatesLabel(filters.collectedDates, t, language)}
      </>
    ),
    Icon: CoinsIcon,
    name: billingFilterNames.collectedDates,
  },
  {
    show: filters => filters.exchangeForOrderStatus,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado de pedido de cambio')}: </strong>
        {t(getExchangeForOrderStatusName(filters.exchangeForOrderStatus))}
      </>
    ),
    Icon: StorefrontIcon,
    name: returnsFilterNames.exchangeForOrderStatus,
  },
  {
    show: filters => filters.paymentStatus,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado de pago')}: </strong>
        {t(getPaymentStatusName(filters.paymentStatus))}
      </>
    ),
    Icon: MoneyIcon,
    name: returnsFilterNames.paymentStatus,
  },
  {
    show: filters => filters.refundStatus,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado de reembolso')}: </strong>
        {t(getRefundStatusName(filters.refundStatus))}
      </>
    ),
    Icon: MoneyIcon,
    name: returnsFilterNames.refundStatus,
  },
  {
    show: filters => getAmountMatchType(filters, amountFilterConfig[returnsFilterNames.refundTotalAmount]) !== null,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Importe total del reembolso')}: </strong>
        {getAmountFilterLabel(filters, amountFilterConfig[returnsFilterNames.refundTotalAmount])}
      </>
    ),
    Icon: CoinsIcon,
    name: returnsFilterNames.refundTotalAmount,
  },
  {
    show: filters => getAmountMatchType(filters, amountFilterConfig[returnsFilterNames.paymentTotalAmount]) !== null,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Importe total del pago')}: </strong>
        {getAmountFilterLabel(filters, amountFilterConfig[returnsFilterNames.paymentTotalAmount])}
      </>
    ),
    Icon: CoinsIcon,
    name: returnsFilterNames.paymentTotalAmount,
  },
  {
    show: filters => filters.variantsSkus && filters.variantsSkus.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Contiene productos')}: </strong>
        {truncateList(filters.variantsSkus, t)}
      </>
    ),
    Icon: TShirtIcon,
    name: returnsFilterNames.variantsSkus,
  },
  {
    show: filters => filters.isGift,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Regalos')}: </strong>
        {getIsGiftLabel(filters.isGift, t)}
      </>
    ),
    Icon: GiftIcon,
    name: returnsFilterNames.isGift,
  },
  {
    show: filters => filters.transactionTags && filters.transactionTags.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Etiquetas de transacción')}: </strong>
        {truncateList(filters.transactionTags, t)}
      </>
    ),
    Icon: TagIcon,
    name: returnsFilterNames.transactionTags,
  },
  {
    show: filters => filters.policyRules && filters.policyRules.length > 0,
    label: ({ filters, t, policyRules }) => (
      <>
        <strong>{t('Policy Rules aplicadas')}: </strong>
        {getPolicyRulesNames(filters.policyRules, policyRules, t)}
      </>
    ),
    Icon: WarningCircleIcon,
    name: returnsFilterNames.policyRules,
  },
  {
    show: filters => filters.hasPolicyRules,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tiene Policy Rules aplicadas')}: </strong>
        {filters.hasPolicyRules === 'true' ? t('Con Policy Rules aplicadas') : t('Sin Policy Rules aplicadas')}
      </>
    ),
    Icon: WarningCircleIcon,
    name: returnsFilterNames.hasPolicyRules,
  },
  {
    show: filters => filters.shippingLabelStatus,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado etiquetas de devolución')}: </strong>
        {t(getShippingLabelStatusName(filters.shippingLabelStatus))}
      </>
    ),
    Icon: PackageIcon,
    name: returnsFilterNames.shippingLabelStatus,
  },
  {
    show: filters => filters.parcelType?.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipo de envío')}: </strong>
        {getParcelTypeLabels(filters.parcelType, t)}
      </>
    ),
    Icon: TagIcon,
    name: parcelFilterNames.parcelType,
  },
  {
    show: filters => filters.shipmentStatus && filters.shipmentStatus.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado')}: </strong>
        {getShipmentStatusLabels(filters.shipmentStatus, t)}
      </>
    ),
    Icon: CircleDashedIcon,
    name: parcelFilterNames.shipmentStatus,
  },
  {
    show: filters => filters.shipmentNormalizedTrackingStatus && filters.shipmentNormalizedTrackingStatus.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Último estado de seguimiento')}: </strong>
        {getNormalizedTrackingStatusLabels(filters.shipmentNormalizedTrackingStatus, t)}
      </>
    ),
    Icon: CircleDashedIcon,
    name: parcelFilterNames.shipmentNormalizedTrackingStatus,
  },
  {
    show: filters => filters.shipmentOrigin && filters.shipmentOrigin.length,
    label: ({ filters, t, regions }) => (
      <>
        <strong>{t('Origen')}: </strong>
        {getRegionsNames(filters.shipmentOrigin, regions, t)}
      </>
    ),
    Icon: AirplaneTakeoffIcon,
    name: parcelFilterNames.shipmentOrigin,
  },
  {
    show: filters => filters.shipmentDestination && filters.shipmentDestination.length,
    label: ({ filters, t, regions }) => (
      <>
        <strong>{t('Destino')}: </strong>
        {getRegionsNames(filters.shipmentDestination, regions, t)}
      </>
    ),
    Icon: AirplaneLandingIcon,
    name: parcelFilterNames.shipmentDestination,
  },
  {
    show: filters => filters.creationDates && (filters.creationDates.startDate || filters.creationDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de creación')}: </strong>
        {getDatesLabel(filters.creationDates, t, language)}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: parcelFilterNames.creationDate,
  },
  {
    show: filters =>
      filters.exchangeCreatedDates && (filters.exchangeCreatedDates.startDate || filters.exchangeCreatedDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de creación pedido de cambio')}: </strong>
        {getDatesLabel(filters.exchangeCreatedDates, t, language)}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.exchangeCreatedDate,
  },
  {
    show: filters => filters.exchangeCreatedDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de creación pedido de cambio')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.exchangeCreatedDaysGte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.exchangeCreatedDaysGte,
  },
  {
    show: filters => filters.exchangeCreatedDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de creación pedido de cambio')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.exchangeCreatedDaysLte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.exchangeCreatedDaysLte,
  },
  {
    show: filters => filters.shipmentException,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Alertas pendientes')}: </strong>
        {getShipmentExceptionsOptions(filters.shipmentException, t)}
      </>
    ),
    Icon: WarningIcon,
    name: parcelFilterNames.shipmentException,
  },
  {
    show: filters => filters.shipmentCreationDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de creación')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.shipmentCreationDaysGte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: parcelFilterNames.shipmentCreationDaysGte,
  },
  {
    show: filters => filters.shipmentCreationDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de creación')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.shipmentCreationDaysLte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: parcelFilterNames.shipmentCreationDaysLte,
  },
  {
    show: filters => filters.hasAlerts,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tiene alertas pendientes')}: </strong>
        {filters.hasAlerts === 'true' ? t('Con alertas pendientes') : t('Sin alertas pendientes')}
      </>
    ),
    Icon: WarningIcon,
    name: returnsFilterNames.hasAlerts,
  },
  {
    show: filters => filters.pendingAlertTypes?.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipos de alerta')}: </strong>
        {getAlertTypeLabels(filters.pendingAlertTypes, t)}
      </>
    ),
    Icon: WarningIcon,
    name: returnsFilterNames.pendingAlertTypes,
  },
  {
    show: filters => filters.missing,
    label: ({ filters, t }) => (
      <>
        <strong>{getMissingInformationLabel(filters.missing, t)}: </strong>
        {getMissingInformationValue(filters.missing, t)}
      </>
    ),
    Icon: WarningIcon,
    name: inventoryFilterNames.missing,
  },
  {
    show: filters => filters.country,
    label: ({ filters, t, regions }) => (
      <>
        <strong>{t('País de origen')}: </strong>
        {getCountryName(filters.country, regions)}
      </>
    ),
    Icon: GlobeHemisphereWestIcon,
    name: inventoryFilterNames.country,
  },
  {
    show: filters => filters.weightLte || filters.weightGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Peso')}: </strong>
        {getWeightLabel(filters.weightGte, filters.weightLte)}
      </>
    ),
    Icon: ScalesIcon,
    name: inventoryFilterNames.weightLte,
  },
  {
    show: filters => filters.unitCostLte || filters.unitCostGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Coste unitario')}: </strong>
        {getUnitCostLabel(filters.unitCostGte, filters.unitCostLte)}
      </>
    ),
    Icon: CoinsIcon,
    name: inventoryFilterNames.unitCostLte,
  },
  {
    show: filters => filters.category,
    label: ({ filters, productCategories, t }) => (
      <>
        <strong>{t('Categoría')}: </strong>
        {getCategoryLabel(filters.category, productCategories)}
      </>
    ),
    Icon: PackageIcon,
    name: inventoryFilterNames.category,
  },
  {
    show: filters => filters.productStatus,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado del producto')}: </strong>
        {getProductStatusLabel(filters.productStatus, t)}
      </>
    ),
    Icon: PackageCheckIcon,
    name: inventoryFilterNames.productStatus,
  },
  {
    show: filters => filters.hasExportPrice?.length > 0,
    label: ({ filters, t }) => {
      return (
        <>
          <strong>{t('Export price')}: </strong>
          {getExportPriceStatusLabels(filters.hasExportPrice, t)}
        </>
      )
    },
    Icon: CoinsIcon,
    name: inventoryFilterNames.hasExportPrice,
  },
  {
    show: filters => filters.exportPriceCountry?.length > 0,
    label: ({ filters, t, regions }) => {
      return (
        <>
          <strong>{t('Export price')}: </strong>
          {getCountryNames(filters.exportPriceCountry, regions, t)}
        </>
      )
    },
    Icon: CoinsIcon,
    name: inventoryFilterNames.exportPriceCountry,
  },
  {
    show: filters => filters.shipmentsCarrier?.length > 0,
    label: ({ filters, carriers, t }) => (
      <>
        <strong>{t('Transportista')}: </strong>
        {getCarriersLabel(filters.shipmentsCarrier, carriers, t)}
      </>
    ),
    Icon: TruckIcon,
    name: parcelFilterNames.shipmentsCarrier,
  },
  {
    show: filters => filters.shipmentsCarrierService,
    label: ({ filters, carrierServices, t }) => (
      <>
        <strong>{t('Servicio de logística')}: </strong>
        {getCarrierServiceLabel(filters.shipmentsCarrierService, carrierServices)}
      </>
    ),
    Icon: TruckIcon,
    name: parcelFilterNames.shipmentsCarrierService,
  },
  {
    show: filters => filters.carrier?.length > 0,
    label: ({ filters, carriers, t }) => (
      <>
        <strong>{t('Transportista devolución')}: </strong>
        {getCarriersLabel(filters.carrier, carriers, t)}
      </>
    ),
    Icon: TruckIcon,
    name: returnsFilterNames.carrier,
  },
  {
    show: filters =>
      filters.shipmentExternalCreatedDates &&
      (filters.shipmentExternalCreatedDates.startDate || filters.shipmentExternalCreatedDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de pedido')}: </strong>
        {getDatesLabel(filters.shipmentExternalCreatedDates, t, language)}
      </>
    ),
    Icon: HandbagIcon,
    name: parcelFilterNames.shipmentExternalCreated,
  },
  {
    show: filters => filters.shipmentExternalCreatedDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de pedido')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.shipmentExternalCreatedDaysGte })}
      </>
    ),
    Icon: HandbagIcon,
    name: parcelFilterNames.shipmentExternalCreatedDaysGte,
  },
  {
    show: filters => filters.shipmentExternalCreatedDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de pedido')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.shipmentExternalCreatedDaysLte })}
      </>
    ),
    Icon: HandbagIcon,
    name: parcelFilterNames.shipmentExternalCreatedDaysLte,
  },
  {
    show: filters =>
      filters.shipmentPackedAtDates &&
      (filters.shipmentPackedAtDates.startDate || filters.shipmentPackedAtDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de empaquetado')}: </strong>
        {getDatesLabel(filters.shipmentPackedAtDates, t, language)}
      </>
    ),
    Icon: PackageIcon,
    name: parcelFilterNames.shipmentPackedAt,
  },
  {
    show: filters => filters.shipmentPackedAtDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de empaquetado')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.shipmentPackedAtDaysGte })}
      </>
    ),
    Icon: PackageIcon,
    name: parcelFilterNames.shipmentPackedAtDaysGte,
  },
  {
    show: filters => filters.shipmentPackedAtDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de empaquetado')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.shipmentPackedAtDaysLte })}
      </>
    ),
    Icon: PackageIcon,
    name: parcelFilterNames.shipmentPackedAtDaysLte,
  },
  {
    show: filters =>
      filters.shipmentReadyDate && (filters.shipmentReadyDate.startDate || filters.shipmentReadyDate.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de listo para envío')}: </strong>
        {getDatesLabel(filters.shipmentReadyDate, t, language)}
      </>
    ),
    Icon: ThumbsUpIcon,
    name: parcelFilterNames.shipmentReadyDate,
  },
  {
    show: filters => filters.shipmentReadyDateDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de listo para envío')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.shipmentReadyDateDaysGte })}
      </>
    ),
    Icon: ThumbsUpIcon,
    name: parcelFilterNames.shipmentReadyDateDaysGte,
  },
  {
    show: filters => filters.shipmentReadyDateDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de listo para envío')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.shipmentReadyDateDaysLte })}
      </>
    ),
    Icon: ThumbsUpIcon,
    name: parcelFilterNames.shipmentReadyDateDaysLte,
  },
  {
    show: filters =>
      filters.shipmentShippedAtDates &&
      (filters.shipmentShippedAtDates.startDate || filters.shipmentShippedAtDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de envío')}: </strong>
        {getDatesLabel(filters.shipmentShippedAtDates, t, language)}
      </>
    ),
    Icon: PackageShippedIcon,
    name: parcelFilterNames.shipmentShippedAt,
  },
  {
    show: filters => filters.shipmentShippedAtDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de envío')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.shipmentShippedAtDaysGte })}
      </>
    ),
    Icon: PackageShippedIcon,
    name: parcelFilterNames.shipmentShippedAtDaysGte,
  },
  {
    show: filters => filters.shipmentShippedAtDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de envío')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.shipmentShippedAtDaysLte })}
      </>
    ),
    Icon: PackageShippedIcon,
    name: parcelFilterNames.shipmentShippedAtDaysLte,
  },
  {
    show: filters =>
      filters.shipmentDeliveredAtDates &&
      (filters.shipmentDeliveredAtDates.startDate || filters.shipmentDeliveredAtDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de entrega')}: </strong>
        {getDatesLabel(filters.shipmentDeliveredAtDates, t, language)}
      </>
    ),
    Icon: PackageCheckIcon,
    name: parcelFilterNames.shipmentDeliveredAt,
  },
  {
    show: filters => filters.shipmentDeliveredAtDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de entrega')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.shipmentDeliveredAtDaysGte })}
      </>
    ),
    Icon: PackageCheckIcon,
    name: parcelFilterNames.shipmentDeliveredAtDaysGte,
  },
  {
    show: filters => filters.shipmentDeliveredAtDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de entrega')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.shipmentDeliveredAtDaysLte })}
      </>
    ),
    Icon: PackageCheckIcon,
    name: parcelFilterNames.shipmentDeliveredAtDaysLte,
  },
  {
    show: filters => filters.exchangedType?.length,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipo de cambio solicitado')}: </strong>
        {getExchangeTypeOptions(filters.exchangedType, t)}
      </>
    ),
    Icon: ArrowsLeftRightIcon,
    name: returnsFilterNames.exchangedType,
  },
  {
    show: filters =>
      filters.refundCreatedDates && (filters.refundCreatedDates.startDate || filters.refundCreatedDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha emisión reembolso')}: </strong>
        {getDatesLabel(filters.refundCreatedDates, t, language)}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.refundCreatedDate,
  },
  {
    show: filters => filters.refundCreatedDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha emisión reembolso')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.refundCreatedDaysGte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.refundCreatedDaysGte,
  },
  {
    show: filters => filters.refundCreatedDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha emisión reembolso')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.refundCreatedDaysLte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: returnsFilterNames.refundCreatedDaysLte,
  },
  {
    show: filters => filters.carrierService,
    label: ({ filters, t, carriers }) => (
      <>
        <strong>{t('Servicio de logística')}: </strong>
        {getCarrierServiceLabel(filters.carrierService, carriers)}
      </>
    ),
    Icon: TruckIcon,
    name: configFilterNames.carrierService,
  },
  {
    show: filters => filters.productCategory && filters.productCategory.length,
    label: ({ filters, t, productCategories }) => (
      <>
        <strong>{t('Categoría de producto')}: </strong>
        {getNamesByIds(filters.productCategory, productCategories, t)}
      </>
    ),
    Icon: TShirtIcon,
    name: returnsFilterNames.productCategory,
  },
  {
    show: filters => filters.productCollection && filters.productCollection.length,
    label: ({ filters, t, productCollections }) => (
      <>
        <strong>{t('Colección')}: </strong>
        {getNamesByIds(filters.productCollection, productCollections, t)}
      </>
    ),
    Icon: TShirtIcon,
    name: returnsFilterNames.productCollection,
  },
  {
    show: filters => filters.productType && filters.productType.length,
    label: ({ filters, t, productTypes }) => (
      <>
        <strong>{t('Tipo de producto')}: </strong>
        {getNamesByIds(filters.productType, productTypes, t)}
      </>
    ),
    Icon: TShirtIcon,
    name: returnsFilterNames.productType,
  },
  {
    show: filters => filters.orderType,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipo de pedido')}: </strong>
        {t(orderTypeMenuItems.find(item => item.value === filters.orderType)?.label)}
      </>
    ),
    Icon: HandbagIcon,
    name: returnsFilterNames.orderType,
  },
  {
    show: filters => filters.shipmentLocation,
    label: ({ filters, t, locations }) => (
      <>
        <strong>{t('Ubicación de origen')}: </strong>
        {getLocationLabel(filters.shipmentLocation, locations)}
      </>
    ),
    Icon: WarehouseIcon,
    name: parcelFilterNames.shipmentLocation,
  },
  {
    show: filters => filters.claimStatus?.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Estado de la incidencia')}: </strong>
        {getClaimStatusLabels(filters.claimStatus, t)}
      </>
    ),
    Icon: CircleDashedIcon,
    name: parcelFilterNames.claimStatus,
  },
  {
    show: filters => filters.claimType?.length > 0,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Tipo de incidencia')}: </strong>
        {getClaimTypeLabels(filters.claimType, t)}
      </>
    ),
    Icon: ChatAlertIcon,
    name: parcelFilterNames.claimType,
  },
  {
    show: filters =>
      filters.claimCreatedAtDates && (filters.claimCreatedAtDates.startDate || filters.claimCreatedAtDates.endDate),
    label: ({ filters, t, language }) => (
      <>
        <strong>{t('Fecha de creación de la incidencia')}: </strong>
        {getDatesLabel(filters.claimCreatedAtDates, t, language)}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: parcelFilterNames.claimCreatedAt,
  },
  {
    show: filters => filters.claimCreatedAtDaysGte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de creación de la incidencia')}: </strong>
        {t('Hace {{days}} días o más', { days: filters.claimCreatedAtDaysGte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: parcelFilterNames.claimCreatedAtDaysGte,
  },
  {
    show: filters => filters.claimCreatedAtDaysLte,
    label: ({ filters, t }) => (
      <>
        <strong>{t('Fecha de creación de la incidencia')}: </strong>
        {t('Hace {{days}} días o menos', { days: filters.claimCreatedAtDaysLte })}
      </>
    ),
    Icon: CalendarBlankIcon,
    name: parcelFilterNames.claimCreatedAtDaysLte,
  },
]
